import { DAILY_STATISTICS_OPTIONS, statisticsSession } from '../../api/statisticsQueries';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Database, ExternalLink, RefreshCw, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getCostDashboard } from '../../api/costScale';
import { getGaDashboard } from '../../api/gaAdmin';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import { useLocale } from '../../context/LocaleContext';
import {
  allocationCoverage,
  allocationScope,
  buildCostScenarios,
  formatMoney,
  latestGaBaseline,
  projectLineItem,
  projectionRuleCoverage,
  snapshotAgeDays,
  sortLineItems,
} from '../../utils/costScale';
import { getGaDateRange } from '../../utils/gaDashboard';
import { t } from '../../utils/i18n';
import DashboardKpi from '../Dashboard/components/DashboardKpi';
import DashboardPanel from '../Dashboard/components/DashboardPanel';
import CostPlanning from './CostPlanning';

function StatusBadge({ tone = 'neutral', children }) {
  const tones = {
    neutral: 'border-white/10 bg-white/5 text-dark-text-secondary',
    success: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300',
    warning: 'border-amber-400/30 bg-amber-400/10 text-amber-200',
    danger: 'border-red-400/30 bg-red-400/10 text-red-200',
    primary: 'border-primary/30 bg-primary/10 text-primary',
  };
  return <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${tones[tone]}`}>{children}</span>;
}

function formatCompact(value) {
  if (!Number.isFinite(Number(value))) return '—';
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(Number(value));
}

function scenarioLabel(mau, locale) {
  if (locale !== 'ko') return formatCompact(mau);
  if (mau >= 1_000_000) return '100만';
  if (mau >= 100_000) return '10만';
  return '1만';
}

function EmptySnapshot({ environment }) {
  return (
    <div className="rounded-xl border border-amber-400/25 bg-amber-400/5 p-5">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
        <div>
          <h2 className="font-semibold text-dark-text">{t('costScale.empty.title')}</h2>
          <p className="mt-1 text-sm leading-6 text-dark-text-secondary">
            {t('costScale.empty.description', { environment })}
          </p>
          <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-dark-text-secondary">
            <li>{t('costScale.empty.stepDownload')}</li>
            <li>{t('costScale.empty.stepNormalize')}</li>
            <li>{t('costScale.empty.stepUpload')}</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

function triggerTone(trigger) {
  if (trigger === 'scheduled') return 'warning';
  if (trigger === 'admin') return 'primary';
  if (trigger === 'unknown') return 'danger';
  return 'neutral';
}

function UsageAttribution({ attribution, numberLocale }) {
  const metrics = attribution?.metrics || [];
  if (metrics.length === 0) return null;

  return (
    <section className="space-y-3" aria-labelledby="usage-attribution-title">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 id="usage-attribution-title" className="text-lg font-semibold text-dark-text">{t('costScale.usageAttribution.title')}</h2>
          <StatusBadge tone="success">{t('costScale.status.measured')}</StatusBadge>
          <StatusBadge>{t('costScale.readOnly')}</StatusBadge>
          {attribution.environment && <StatusBadge>{t('costScale.usageAttribution.environment', { environment: attribution.environment.toUpperCase() })}</StatusBadge>}
        </div>
        <p className="mt-1 text-sm text-dark-text-secondary">
          {t('costScale.usageAttribution.description', { from: attribution.period?.from || '—', to: attribution.period?.to || '—' })}
        </p>
        <p className="mt-1 text-xs text-amber-200">{t('costScale.usageAttribution.costBoundary')}</p>
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {metrics.map((metric) => (
          <DashboardPanel
            key={`${metric.source}-${metric.metric}`}
            title={t(`costScale.usageAttribution.metric.${metric.metric}`)}
            subtitle={t('costScale.usageAttribution.metricSummary', {
              total: new Intl.NumberFormat(numberLocale).format(Number(metric.total || 0)),
              coverage: `${(Number(metric.coverage?.mapped_share || 0) * 100).toFixed(1)}%`,
            })}
          >
            <div className="mb-4 flex flex-wrap gap-2">
              {(metric.capabilities || []).slice(0, 8).map((item) => (
                <span key={item.capability} className="rounded-md border border-dark-border bg-dark-surface px-2.5 py-1.5 text-xs text-dark-text-secondary">
                  <strong className="text-dark-text">{item.capability}</strong>
                  {' '}{(Number(item.share || 0) * 100).toFixed(1)}%
                </span>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead className="border-b border-dark-border text-xs text-dark-text-muted">
                  <tr>
                    <th className="px-2 py-2 font-medium">{t('costScale.usageAttribution.resource')}</th>
                    <th className="px-2 py-2 font-medium">{t('costScale.usageAttribution.capability')}</th>
                    <th className="px-2 py-2 font-medium">{t('costScale.usageAttribution.trigger')}</th>
                    <th className="px-2 py-2 text-right font-medium">{t('costScale.usageAttribution.count')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-border/70">
                  {(metric.resources || []).map((resource) => (
                    <tr key={`${metric.source}-${resource.resource_name}`} className="text-dark-text-secondary">
                      <td className="max-w-[20rem] break-words px-2 py-2 font-mono text-xs text-dark-text">{resource.resource_name}</td>
                      <td className="px-2 py-2">{resource.capability || t('costScale.table.unallocated')}</td>
                      <td className="px-2 py-2"><StatusBadge tone={triggerTone(resource.trigger_type)}>{t(`costScale.trigger.${resource.trigger_type}`)}</StatusBadge></td>
                      <td className="px-2 py-2 text-right tabular-nums">{new Intl.NumberFormat(numberLocale).format(Number(resource.value || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DashboardPanel>
        ))}
      </div>
    </section>
  );
}

export function CostScaleContent({ snapshot, gaRows = [], environment = 'prod' }) {
  const { locale } = useLocale();
  const [showAllItems, setShowAllItems] = useState(false);
  const numberLocale = locale === 'ko' ? 'ko-KR' : 'en-US';
  const baseline = useMemo(() => latestGaBaseline(gaRows, snapshot?.baseline), [gaRows, snapshot]);
  const scenarios = useMemo(() => buildCostScenarios(snapshot, baseline), [baseline, snapshot]);
  const items = useMemo(() => sortLineItems(snapshot?.line_items), [snapshot]);
  const coverage = allocationCoverage(items);
  const platformCoverage = allocationCoverage(items, 'platform');
  const ruleCoverage = projectionRuleCoverage(items);
  const fallbackLines = items.filter((item) => item.classification?.projection_rule === 'unclassified-held-fixed').length;
  const ageDays = snapshotAgeDays(snapshot);
  const currency = snapshot?.currency || 'USD';
  const forecast = Number(snapshot?.summary?.monthly_forecast || items.reduce((sum, item) => sum + Number(item.monthly_cost || 0), 0));
  const mtd = Number(snapshot?.summary?.month_to_date || items.reduce((sum, item) => sum + Number(item.actual_cost || 0), 0));
  const previous = Number(snapshot?.summary?.previous_month_cost || 0);
  const change = previous > 0 ? (forecast - previous) / previous : null;
  const perMau = baseline.mau > 0 ? forecast / baseline.mau : null;
  const isStale = ageDays != null && ageDays > 8;
  const chartData = scenarios.map((scenario) => ({ name: scenarioLabel(scenario.mau, locale), cost: scenario.cost }));
  const visibleItems = showAllItems ? items : items.slice(0, 25);

  if (!snapshot) return <EmptySnapshot environment={environment.toUpperCase()} />;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <DashboardKpi label={t('costScale.kpi.forecast')} value={formatMoney(forecast, currency, numberLocale)} accent size="primary" hint={change == null ? t('costScale.kpi.noComparison') : t('costScale.kpi.previousChange', { value: `${change >= 0 ? '+' : ''}${(change * 100).toFixed(1)}%` })} />
        <DashboardKpi label={t('costScale.kpi.monthToDate')} value={formatMoney(mtd, currency, numberLocale)} size="primary" hint={`${snapshot.period?.from || '—'} ~ ${snapshot.period?.to || '—'}`} />
        <DashboardKpi label="DAU" value={formatCompact(baseline.dau)} size="primary" hint={t('costScale.kpi.liveGa')} />
        <DashboardKpi label="MAU" value={formatCompact(baseline.mau)} size="primary" hint={t('costScale.kpi.liveGa')} />
        <DashboardKpi label={t('costScale.kpi.costPerMau')} value={perMau == null ? '—' : formatMoney(perMau, currency, numberLocale, 2)} size="primary" hint={t('costScale.kpi.estimate')} />
        <DashboardKpi label={t('costScale.kpi.allocation')} value={`${Math.round(coverage * 100)}%`} size="primary" hint={t('costScale.kpi.allocationHint')} />
      </div>

      <p className="text-sm text-dark-text-secondary">{t('costScale.accountCostBoundary')}</p>
      <CostPlanning />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <DashboardPanel className="xl:col-span-2" title={t('costScale.scenarios.title')} subtitle={t('costScale.scenarios.description')}>
          <details>
          <summary className="cursor-pointer text-sm font-medium text-dark-text-secondary">{t('costScale.scenarios.showReference')}</summary>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {scenarios.map((scenario) => (
              <div key={scenario.mau} className="rounded-lg border border-dark-border bg-dark-surface p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-dark-text">MAU {scenarioLabel(scenario.mau, locale)}</p>
                  <StatusBadge tone="warning">{t('costScale.status.estimated')}</StatusBadge>
                </div>
                <p className="mt-3 text-2xl font-bold text-dark-text">{scenario.cost == null ? '—' : formatMoney(scenario.cost, currency, numberLocale)}</p>
                <p className="mt-1 text-xs text-dark-text-muted">{t('costScale.scenarios.assumption')}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="rgb(var(--admin-border))" vertical={false} />
                <XAxis dataKey="name" stroke="rgb(var(--admin-secondary))" fontSize={12} />
                <YAxis stroke="rgb(var(--admin-secondary))" fontSize={12} tickFormatter={formatCompact} width={54} />
                <Tooltip formatter={(value) => formatMoney(value, currency, numberLocale)} contentStyle={{ background: 'rgb(var(--admin-panel))', border: '1px solid rgb(var(--admin-border))', borderRadius: 8 }} />
                <Bar dataKey="cost" fill="rgb(var(--admin-accent))" radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          </details>
        </DashboardPanel>

        <DashboardPanel title={t('costScale.quality.title')} subtitle={t('costScale.quality.description')}>
          <dl className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-3"><dt className="text-dark-text-secondary">{t('costScale.quality.freshness')}</dt><dd><StatusBadge tone={isStale ? 'danger' : 'success'}>{ageDays == null ? '—' : t('costScale.quality.daysAgo', { value: ageDays })}</StatusBadge></dd></div>
            <div className="flex items-center justify-between gap-3"><dt className="text-dark-text-secondary">{t('costScale.quality.lineItems')}</dt><dd className="font-semibold text-dark-text">{items.length}</dd></div>
            <div className="flex items-center justify-between gap-3"><dt className="text-dark-text-secondary">{t('costScale.quality.measured')}</dt><dd className="font-semibold text-dark-text">{items.filter((item) => item.confidence === 'measured').length}</dd></div>
            <div className="flex items-center justify-between gap-3"><dt className="text-dark-text-secondary">{t('costScale.quality.ruleCoverage')}</dt><dd className="font-semibold text-dark-text">{`${(ruleCoverage * 100).toFixed(1)}%`}</dd></div>
            <div className="flex items-center justify-between gap-3"><dt className="text-dark-text-secondary">{t('costScale.quality.fallbackLines')}</dt><dd className="font-semibold text-dark-text">{fallbackLines}</dd></div>
            <div className="flex items-center justify-between gap-3"><dt className="text-dark-text-secondary">{t('costScale.quality.platformMapped')}</dt><dd className="font-semibold text-dark-text">{`${Math.round(platformCoverage * 100)}%`}</dd></div>
            <div className="flex items-center justify-between gap-3"><dt className="text-dark-text-secondary">{t('costScale.quality.unallocated')}</dt><dd className="font-semibold text-dark-text">{formatMoney(forecast * (1 - coverage), currency, numberLocale)}</dd></div>
          </dl>
          <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs leading-5 text-dark-text-secondary">
            {t('costScale.quality.rule')}
          </div>
        </DashboardPanel>
      </div>

      <UsageAttribution attribution={snapshot.usage_attribution} numberLocale={numberLocale} />

      <DashboardPanel
        title={t('costScale.drivers.title')}
        subtitle={t('costScale.drivers.description', { shown: visibleItems.length, total: items.length })}
        action={items.length > 25 ? <button type="button" onClick={() => setShowAllItems((value) => !value)} className="shrink-0 rounded-md border border-dark-border px-2.5 py-1.5 text-xs font-medium text-dark-text-secondary hover:border-primary hover:text-primary">{showAllItems ? t('costScale.drivers.showTop') : t('costScale.drivers.showAll')}</button> : null}
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1050px] text-left text-sm">
            <thead className="border-b border-dark-border text-xs text-dark-text-muted">
              <tr>
                <th className="px-3 py-3 font-medium">{t('costScale.table.service')}</th>
                <th className="px-3 py-3 font-medium">{t('costScale.table.actual')}</th>
                <th className="px-3 py-3 font-medium">{t('costScale.table.monthly')}</th>
                <th className="px-3 py-3 font-medium">{t('costScale.table.usage')}</th>
                <th className="px-3 py-3 font-medium">{t('costScale.table.driver')}</th>
                <th className="px-3 py-3 font-medium">{t('costScale.table.capability')}</th>
                {scenarios.map((scenario) => <th key={scenario.mau} className="px-3 py-3 font-medium">MAU {scenarioLabel(scenario.mau, locale)}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-border/70">
              {visibleItems.map((item) => (
                <tr key={item.id} className="text-dark-text-secondary hover:bg-white/[0.02]">
                  <td className="px-3 py-3"><p className="font-semibold text-dark-text">{item.service}</p><p className="mt-0.5 text-xs text-dark-text-muted">{item.provider} · {item.region || t('costScale.table.global')}</p>{item.note && <p className="mt-1 max-w-sm break-words text-xs text-dark-text-muted">{item.note}</p>}</td>
                  <td className="px-3 py-3 tabular-nums">{formatMoney(item.actual_cost, currency, numberLocale, 2)}</td>
                  <td className="px-3 py-3 tabular-nums text-dark-text">{formatMoney(item.monthly_cost, currency, numberLocale, 2)}</td>
                  <td className="px-3 py-3 tabular-nums">{item.usage ? `${formatCompact(item.usage.value)} ${item.usage.unit}` : '—'}</td>
                  <td className="px-3 py-3"><StatusBadge tone={item.driver === 'fixed' ? 'neutral' : 'primary'}>{t(`costScale.driver.${item.driver}`)}</StatusBadge><p className="mt-1 max-w-[14rem] break-words text-xs text-dark-text-muted">{item.classification?.projection_rule || t('costScale.table.legacyRule')}</p></td>
                  <td className="px-3 py-3"><p>{(allocationScope(item) === 'platform' ? item.classification?.allocation_labels : item.capabilities)?.join(', ') || t('costScale.table.unallocated')}</p><p className="mt-0.5 text-xs text-dark-text-muted">{t(`costScale.scope.${allocationScope(item)}`)} · {t(`costScale.status.${item.confidence}`)}</p></td>
                  {scenarios.map((scenario) => <td key={scenario.mau} className="px-3 py-3 tabular-nums">{formatMoney(projectLineItem(item, scenario.mau, baseline), currency, numberLocale, 2)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DashboardPanel>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <DashboardPanel title={t('costScale.source.title')}>
          <dl className="grid grid-cols-[9rem_1fr] gap-x-3 gap-y-2 text-sm">
            <dt className="text-dark-text-muted">{t('costScale.source.provider')}</dt><dd className="text-dark-text">{snapshot.source?.provider || '—'}</dd>
            <dt className="text-dark-text-muted">{t('costScale.source.report')}</dt><dd className="text-dark-text">{snapshot.source?.report || '—'}</dd>
            <dt className="text-dark-text-muted">{t('costScale.source.collectedAt')}</dt><dd className="text-dark-text">{snapshot.source?.collected_at || '—'}</dd>
            <dt className="text-dark-text-muted">{t('costScale.source.rules')}</dt><dd className="text-dark-text">{snapshot.classification_rules ? `${snapshot.classification_rules.name} v${snapshot.classification_rules.version}` : t('costScale.table.legacyRule')}</dd>
            <dt className="text-dark-text-muted">Snapshot ID</dt><dd className="break-all font-mono text-xs text-primary">{snapshot.snapshot_id}</dd>
          </dl>
        </DashboardPanel>
        <DashboardPanel title={t('costScale.boundary.title')}>
          <div className="flex items-start gap-3 text-sm leading-6 text-dark-text-secondary">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" />
            <p>{t('costScale.boundary.description')}</p>
          </div>
          <a className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" href="https://fancian.atlassian.net/browse/FW-111" target="_blank" rel="noreferrer">FW-111 <ExternalLink className="h-3.5 w-3.5" /></a>
        </DashboardPanel>
      </div>
    </div>
  );
}

export default function CostScale() {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const { apiEnvironment } = useApiEnvironment();
  const { from, to } = useMemo(() => getGaDateRange(30), []);
  const costQuery = useQuery({ ...DAILY_STATISTICS_OPTIONS, queryKey: ['costScale', apiEnvironment, statisticsSession(token)], queryFn: () => getCostDashboard(token), enabled: !!token, retry: false });
  const gaQuery = useQuery({ ...DAILY_STATISTICS_OPTIONS, queryKey: ['gaDashboard', apiEnvironment, from, to, statisticsSession(token)], queryFn: () => getGaDashboard(from, to, token), enabled: !!token });

  useEffect(() => {
    if (costQuery.error?.status === 401 || gaQuery.error?.status === 401) {
      logout();
      navigate('/login', { replace: true });
    }
  }, [costQuery.error, gaQuery.error, logout, navigate]);

  const onRefresh = useCallback(() => Promise.all([costQuery.refetch(), gaQuery.refetch()]), [costQuery, gaQuery]);
  const refreshing = costQuery.isRefetching || gaQuery.isRefetching;

  return (
    <div className="min-h-screen bg-dark-bg px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-none space-y-4">
        <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-dark-border/60 bg-dark-bg/95 py-3 backdrop-blur-sm">
          <div>
            <div className="flex flex-wrap items-center gap-2"><h1 className="text-2xl font-bold text-dark-text sm:text-3xl">{t('costScale.title')}</h1><StatusBadge tone="primary">{apiEnvironment.toUpperCase()}</StatusBadge><StatusBadge>{t('costScale.readOnly')}</StatusBadge></div>
            <p className="mt-1 text-sm text-dark-text-secondary">{t('costScale.subtitle')}</p>
          </div>
          <button type="button" onClick={onRefresh} disabled={refreshing} className="flex shrink-0 items-center gap-2 rounded-lg border border-dark-border px-3 py-2 text-dark-text transition-colors hover:border-primary hover:text-primary disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /><span className="hidden text-sm font-medium sm:inline">{t('dashboard.refresh')}</span>
          </button>
        </header>

        {gaQuery.error && <p className="rounded-lg border border-amber-900/50 bg-amber-950/30 px-4 py-3 text-sm text-amber-200">{t('costScale.gaError')}</p>}
        {costQuery.isLoading ? (
          <div className="flex min-h-[20rem] items-center justify-center text-dark-text-secondary"><Database className="mr-2 h-5 w-5 animate-pulse" />{t('common.loading')}</div>
        ) : (
          <CostScaleContent snapshot={costQuery.data} gaRows={gaQuery.data || []} environment={apiEnvironment} />
        )}
      </div>
    </div>
  );
}
